import { Loading } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import { getEncounterQueryOptions } from "@/queries";
import {
	CreateCaseFormFieldSchema,
	CreateCaseSearchParamSchema,
} from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import dayjs from "dayjs";
import { Suspense, lazy, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

const CreateNewCaseDetail = lazy(
	() => import("../-components/CreateNewCaseDetail"),
);
const CreateNewPatient = lazy(() => import("../-components/CreateNewPatient"));
const CreateNewPatientInfo = lazy(
	() => import("../-components/CreateNewPatientInfo"),
);

export const Route = createFileRoute("/dashboard/case/create/")({
	validateSearch: zodSearchValidator(CreateCaseSearchParamSchema),
	loaderDeps: ({ search }) => ({ encounterId: search.encounter_id }),
	component: RouteComponent,
});

function RouteComponent() {
	const onBack = useBackward();
	const searchQuery = Route.useSearch({
		select: (state) => state as CreateCaseSearchParamSchema,
	});
	const encounterId = searchQuery.encounter_id;

	const encounterQuery = useQuery(
		getEncounterQueryOptions(encounterId as number),
	);

	const form = useForm<CreateCaseFormFieldSchema>({
		mode: "all",
		resolver: zodResolver(CreateCaseFormFieldSchema),
		defaultValues: {
			step: 0,
		},
	});

	const currentStep = form.watch("step", 0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const onClickBack = useCallback(() => {
		if (encounterId) return onBack();
		if (searchQuery) return onBack();
		if (currentStep > 0) {
			return form.setValue("step", currentStep - 1);
		}
		return onBack();
	}, [currentStep, encounterId]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (searchQuery) {
			if (searchQuery.pid) {
				form.setValue("patient.id", searchQuery.pid);
			}
			if (searchQuery.step) {
				form.setValue("step", searchQuery.step);
			}
			if (searchQuery.paddress) {
				form.setValue("patient.address", searchQuery.paddress);
			}
			if (searchQuery.pic) {
				form.setValue("patient.ic_number", searchQuery.pic);
			}
			if (searchQuery.pdob) {
				const birthDate = dayjs(searchQuery.pdob);
				form.setValue("year_of_birth", birthDate.year());
				form.setValue("patient.dob", birthDate.format("DD/MM/YYYY"));
			}
			if (searchQuery.pname) {
				form.setValue("patient.name", searchQuery.pname);
			}
			if (searchQuery.psurname) {
				form.setValue("patient.surname", searchQuery.psurname);
			}
			if (searchQuery.psex) {
				form.setValue("sex", searchQuery.psex as any);
			}
		}
	}, [searchQuery]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (encounterQuery.isSuccess) {
			form.setValue("step", 2);
			if (encounterQuery.data.patient) {
				if (encounterQuery.data.patient.dob) {
					const dayOfBirth = dayjs(encounterQuery.data.patient.dob);
					form.setValue("year_of_birth", dayOfBirth);
				}
				if (encounterQuery.data.patient.sex) {
					form.setValue("sex", encounterQuery.data.patient.sex);
				}
			}
		}
	}, [encounterQuery]);

	return (
		<div className="relative h-full bg-white flex flex-col">
			<BackAppBar onBack={onClickBack} />
			<FormProvider {...form}>
				<Suspense>
					{currentStep === 0 ? <CreateNewPatient /> : null}
					{currentStep === 1 ? <CreateNewPatientInfo /> : null}
					{currentStep === 2 ? (
						<CreateNewCaseDetail encounterId={encounterId} />
					) : null}
				</Suspense>
			</FormProvider>
			<Loading status={encounterQuery.isLoading} />
		</div>
	);
}
